import _ from 'underscore';

import { CommonErrorCode, DataConnectionErrorCode } from '~commonInfra/utils/ErrorCodes';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Formatters, Messages } from '~src/codegen/Localize';
import { ContentActionNotification } from '~src/commonInfra/contentActions/ContentActionNotification';
import { CredentialOption, EditableConnection } from '~src/connectivity/components/EditConnection/IEditConnectionDialog';
import {
  ITestConnectionResultReact
} from '~src/connectivity/components/EditConnection/utils/EditConnectionActionTypes';
import { DatabaseConnectionDetailInfo } from '~src/datasource/dataConnection/models/ConnectionDetailInfo';
import { DataConnections, IConnectionUpdate, WifAuthMode } from '~src/datasource/dataConnection/resources/DataConnections';
import { ICheckConnectionResult } from '~src/datasource/dataConnection/serverApi/CheckConnectionRequest';
import { IDataSourceBase, IDataSourceDetailInfo, IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';
import { DataSources } from '~src/datasource/resources/Datasources';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { isFlowSpace } from '~src/spacesPlatform/state/SpaceInfo';

import { EditConnectionDialogState, EditConnectionRowProps, PerConnectionFieldState } from '../EditConnectionAction';


export const MAX_PORT = 65535;

// A sentinel value to mean mixed text field values
export let TextBoxMixedValues = '———';

// W-22483245: WIF wire values and the GCP service-account format rule. The regex must match the
// server (WifConnectionUpdateValidator) and Desktop (ServiceAccountValidation.h) validators that
// reject the same input on save. The 256 length bound covers GCP service-account emails longer than
// 64 characters, which are legitimate and were previously rejected in the dialog only.
export const WIF_IMPERSONATION: WifAuthMode = 'workload-identity-federation-impersonation';
export const WIF_STANDARD: WifAuthMode = 'workload-identity-federation';
// The BigQuery "authentication" value for a service-account-file credential, which is not a
// WIF-capable credential.
const GBQ_SERVICE_ACCOUNT_AUTHENTICATION = 'auth-oauth-service-account';
const SERVICE_ACCOUNT_REGEX = /^[a-zA-Z0-9+=,.@_-]{1,256}$/;

export namespace EditConnectionReactActionUtils {
  export function makePrompt(numberOfConnections: number) {
    return Messages.editDataConnections_prompt({
      ITEM_COUNT: numberOfConnections,
      ITEM_COUNT_LOC: Formatters.formatCount(numberOfConnections)
    });
  }

  export function isMixed(value: string): boolean {
    return value === TextBoxMixedValues;
  }

  export function uniqueValueOrMixed<T>(items: T[], iterator: _.ListIterator<T, string>) {
    const values = _.map(items, iterator);
    const first = values[0];
    return _.all(values, (val) => val === first) ? first : TextBoxMixedValues;
  }

  export function uniqueValueOrUndefined<T, TResult>(items: T[], iterator: _.ListIterator<T, TResult>) {
    const values = _.map(items, iterator);
    const first = values[0];
    return _.all(values, (val) => val === first) ? first : undefined;
  }


  export async function canEmbedPassword(datasource: IDataSourceInfo): Promise<boolean> {
    if (DataSources.isPublished(datasource)) {
      const isCubeDatasource = await checkCubeDatasource(datasource);
      return !isCubeDatasource;
    } else {
      return true;
    }
  }


  export async function checkEditedConnection(
    connectionId: string,
    dialogState: EditConnectionDialogState
  ): Promise<ICheckConnectionResult> {
    const checkConnectionResult: ICheckConnectionResult | void =
      await DataConnections.checkConnection(connectionId, editConnectionDialogStateToConnectionUpdate(dialogState));
    return checkConnectionResult;
  }

  // W-22483230: positional fallback label. Two connections to the same instance are otherwise
  // indistinguishable in the selector, so a 1-based ordinal keeps the rows distinct.
  export function rowLabel(connection: EditableConnection, index: number): string {
    const caption = (connection.connectionDetails as DatabaseConnectionDetailInfo).caption;
    if (caption) {
      return caption;
    }
    return Messages.editDataConnections_connectionRowLabel({
      CONNECTION_TYPE_NAME: connection.connectionTypeDisplayName,
      ORDINAL: Formatters.formatCount(index + 1)
    });
  }

  // W-22483230: apply each row as its own single-id update so each connection succeeds or fails
  // independently, reusing the existing per-id update path rather than adding a batch API.
  export function applyConnectionEdits(rowProps: EditConnectionRowProps[], rowStates: PerConnectionFieldState[]): Promise<IResult> {
    // Index rowProps by connectionId so the per-row lookup below is O(1) rather than a scan per row.
    const rowPropById = new Map(rowProps.map((p) => [p.connectionId, p]));
    return Promise.all(rowStates.map((rowState) => {
      const rowProp = rowPropById.get(rowState.connectionId);
      // A per-row update can reject rather than resolve with errors: a server business error (e.g. an
      // invalid workgroupAuthMode) comes back with no actionId, so the LongRunningAction chain rejects.
      // Map that rejection to an error IResult so the row still counts as a failure and the summary
      // toast fires -- otherwise Promise.all rejects and notifyAggregatedResult never runs (silent fail).
      return applyRowEdit(rowProp, rowState)
        .catch((reason: any): IResult => rejectionToResult(reason));
    })).then((results: IResult[]) => {
      const aggregated = aggregateResults(results);
      notifyAggregatedResult(results);
      return aggregated;
    });
  }

  // Whether a row's OAuth-embedded/WIF chooser applies to its class. When it does not (the unified
  // dialog's plain database rows), credentialOption/workgroupAuthMode are unused placeholders rather
  // than a real auth selection, so callers must not read them as one.
  export function usesAuthChooser(rowProps: Pick<EditConnectionRowProps, 'isOAuthEmbeddedApplicable' | 'isWifSupported'>): boolean {
    return rowProps.isOAuthEmbeddedApplicable || rowProps.isWifSupported;
  }

  // W-22483245: route one row's save to the endpoint its selected auth radio implies. Only rows whose
  // class exposes the auth chooser (OAuth-embedded and/or WIF) take the OAuth/WIF endpoints; every
  // other class keeps the plain updateConnections path, so a password connection reaching this dialog
  // through the router's non-OAuth branch is never re-routed to an OAuth endpoint.
  function applyRowEdit(rowProp: EditConnectionRowProps | undefined, rowState: PerConnectionFieldState): Promise<IResult> {
    if (!!rowProp && usesAuthChooser(rowProp)) {
      switch (deriveAuthRadio(rowState)) {
        case 'wif':
          // editConnectionDialogStateToConnectionUpdate emits the WIF fields; the SA is sent only for
          // impersonation. This is the same call the WIF-only dialog made before the chooser existed.
          return DataConnections.updateConnections([rowState.connectionId], editConnectionDialogStateToConnectionUpdate(rowState));
        case 'no-auth':
          // Drop the embedded credential (No Authentication). For a stored-WIF row this also clears the
          // Workload Identity Federation mode from all three stores so the reopened connection prompts
          // rather than re-resolving as WIF. isStoredWif is the static prop captured once at dialog open
          // (EditConnectionAction), NOT mutable dialog state, so it still reflects the source auth mode
          // after the user selects No Authentication. W-23495558.
          return DataConnections.updateConnectionsOAuth([rowState.connectionId], undefined, rowState.usingRemoteQueryAgent, !!rowProp?.isStoredWif);
        case 'oauth-embedded':
        default:
          // Stored-WIF -> OAuth requires clear+credentialId together. Without a credentialId the Save
          // button is disabled; refuse here so a null credential cannot clear to NoAuth. Non-stored
          // rows without a credentialId (e.g. managed keychain) still use the remote-query-agent-only
          // update. W-23514330.
          if (rowProp?.isStoredWif) {
            if (!isWifToOauthSaveAllowed(true, rowState.credentialId)) {
              return Promise.reject(new Error('Stored WIF to embedded OAuth requires a credentialId'));
            }
            return DataConnections.updateConnectionsOAuth(
              [rowState.connectionId],
              rowState.credentialId,
              rowState.usingRemoteQueryAgent,
              true);
          }
          return rowState.credentialId
            ? DataConnections.updateConnectionsOAuth([rowState.connectionId], rowState.credentialId, rowState.usingRemoteQueryAgent)
            : DataConnections.updateConnectionUsingRemoteQueryAgent([rowState.connectionId], rowState.usingRemoteQueryAgent);
      }
    }
    return DataConnections.updateConnections([rowState.connectionId], editConnectionDialogStateToConnectionUpdate(rowState));
  }

  // W-22483245: the auth radio a row currently shows, derived from the single source of truth
  // (stored workgroupAuthMode + the OAuth credential model) rather than a separate discriminant.
  // Both WIF modes (standard and impersonation) share the single 'wif' radio value:
  // this answers only *which* radio is checked, so a connector offering either WIF mode lands on the
  // one WIF radio and keeps its save on the WIF endpoint rather than reverting to OAuth. The chooser
  // reads the offered workgroupAuthMode (not this value) to decide the radio's label and whether a
  // service-account field is shown, so the two WIF modes are never conflated where it matters.
  export type EditConnectionAuthRadio = 'oauth-embedded' | 'wif' | 'no-auth';

  export function deriveAuthRadio(
    state: Pick<EditConnectionDialogState, 'workgroupAuthMode' | 'credentialOption'>
  ): EditConnectionAuthRadio {
    if (state.workgroupAuthMode === WIF_IMPERSONATION || state.workgroupAuthMode === WIF_STANDARD) {
      return 'wif';
    }
    if (state.credentialOption === 'none') {
      return 'no-auth';
    }
    return 'oauth-embedded';
  }

  // W-22483245: the unified chooser only offers OAuth-embedded, WIF-impersonation, and No-auth, so its
  // account picker understands only the credential-bearing options (oauth / keypair) plus none. Design
  // R2.5 says a googlebigquery connection should never load as password or mixed, but if one does the
  // chooser would otherwise show the OAuth-embedded radio over an empty dropdown and Save would fall
  // into the managed-keychain no-op. Coerce that unexpected shape to none so the row presents honestly
  // as "no single valid credential" instead. The shared OAuthEditModelLoader is left untouched because
  // the stock OAuth dialog legitimately renders password (Salesforce); coercion belongs to the
  // WIF-eligible seed path only.
  export function coerceUnifiedCredentialOption(option: CredentialOption): CredentialOption {
    return option === 'oauth' || option === 'keypair' || option === 'none' ? option : 'none';
  }

  // W-22483245: the field changes to apply when the user picks an auth radio, returned as one patch so
  // the caller writes the whole row once (writing field-by-field would let only the last write survive
  // because each write starts from the same pre-change row). Switching off impersonation clears the
  // service account so a stale account never rides an OAuth or no-auth save. Switching to
  // impersonation leaves the service account untouched: a stored-WIF connection keeps its
  // pre-populated account (its other radios are disabled, so it cannot have been cleared), and a
  // connection switching into WIF for the first time has none to preserve. The selected OAuth account
  // (credentialId) is deliberately left out of every patch so it survives a No-auth round trip the way
  // the stock OAuth toggle preserves it: returning to OAuth-embedded then re-selects the same account
  // rather than falling into the managed-keychain no-op save with a null credentialId.
  export function authRadioStateChange(radio: EditConnectionAuthRadio, wifModesAvailable: WifAuthMode[]): Partial<EditConnectionDialogState> {
    switch (radio) {
      case 'wif':
        // A connector offers exactly one WIF mode; persist that mode (WIF_STANDARD for Snowflake,
        // WIF_IMPERSONATION for BigQuery) rather than assuming impersonation. The service account is
        // left untouched here; editConnectionDialogStateToConnectionUpdate drops it for standard WIF.
        return { workgroupAuthMode: wifModesAvailable[0] };
      case 'no-auth':
        return { workgroupAuthMode: undefined, authFederationServiceAccount: undefined, credentialOption: 'none' };
      case 'oauth-embedded':
      default:
        return { workgroupAuthMode: undefined, authFederationServiceAccount: undefined, credentialOption: 'oauth' };
    }
  }

  // Normalize an updateConnections rejection into an error IResult. The reason may already be an
  // IResult/IError-bearing payload (server returned errors) or a bare ErrorReason code; either way
  // produce an errors[] so the failure is surfaced to the user rather than swallowed.
  function rejectionToResult(reason: any): IResult {
    if (reason && reason.errors && reason.errors.length) {
      return reason as IResult;
    }
    const error: IError = _.isObject(reason) ? (reason as IError) : { code: reason };
    return { errors: [error] };
  }

  // Collapse the per-row results into one IResult: any row error makes the whole action fail.
  function aggregateResults(results: IResult[]): IResult {
    const errors: IError[] = [];
    results.forEach((result) => {
      if (result && result.errors) {
        errors.push(...result.errors);
      }
    });
    return errors.length ? { errors } : {};
  }

  export function showNetworkType(datasource: IDataSourceBase) {
    // Don't show for Embedded datasources
    if (_.isObject(datasource.workbook)) {
      return false;
    }
    // Don't show for Flow connections - check if we're in a Flow space
    const currentSpaceInfo = selectSpaceInfo(getAppStore().getState());
    if (isFlowSpace(currentSpaceInfo)) {
      return false;
    }
    // Only show if the FF is on
    return true;
  }

  export function isPortValid(port: string): boolean {
    return !port || !!(/^\d*$/.exec(port) && parseInt(port, 10) <= MAX_PORT);
  }

  // W-22483245: WIF modes offered per connection class, mirroring the server validator
  // (WifConnectionUpdateValidator): BigQuery offers impersonation, Snowflake offers standard. Hardcoded
  // allowlist to be replaced by a server-sourced per-class list when that API ships. Snowflake is scoped
  // to OAuth connections (isOAuth): a keypair or username-password Snowflake returns no WIF modes so it
  // keeps its current routing and never surfaces the WIF radio -- WIF is only reachable from OAuth.
  export function wifModesForConnectionType(connectionType: string | undefined, isOAuth: boolean, authenticationType?: string): WifAuthMode[] {
    if (connectionType === 'googlebigquery') {
      // A service-account-file credential is not WIF-capable.
      return authenticationType === GBQ_SERVICE_ACCOUNT_AUTHENTICATION ? [] : [WIF_IMPERSONATION];
    }
    if (connectionType === 'snowflake' && isOAuth) {
      return [WIF_STANDARD];
    }
    return [];
  }

  // A selection is WIF-eligible only when EVERY connection is. A shared connection class does not imply
  // shared WIF-eligibility: Snowflake gates on connectionOAuth, so a same-class selection can mix an
  // eligible OAuth row with an ineligible keypair row.
  export function isWifEligible(connections: EditableConnection[]): boolean {
    const wifEditingEnabledForSite = SessionSiteSelectors.isWifConnectionEditingEnabled(getAppStore().getState());
    if (!wifEditingEnabledForSite) {
      return false;
    }
    return connections.every(c => wifModesForConnectionType(c.connectionType, c.connectionOAuth,
      c.connectionDetails?.type === 'database' ? c.connectionDetails.authenticationType : undefined).length > 0);
  }

  // W-22483245: whether the OAuth-embedded (account picker) radio applies to a connection class.
  // Hardcoded allowlist mirroring the WIF allowlist above. To be replaced by a server-sourced per-class
  // list when that API ships. Snowflake is scoped to OAuth connections (isOAuth) so a keypair or
  // username-password Snowflake never sprouts an auth chooser it did not have before.
  export function isOAuthEmbeddedApplicable(connectionType: string | undefined, isOAuth: boolean): boolean {
    return connectionType === 'googlebigquery' || (connectionType === 'snowflake' && isOAuth);
  }

  // Which Edit Connection fields apply to a connection class. Defaults to showing every field, so an
  // unrecognized connector is never accidentally stripped of inputs.
  export interface ConnectionFieldApplicability {
    serverName: boolean;
    serverPort: boolean;
    username: boolean;
    embeddedPassword: boolean;
  }

  // Classes with no host/port/username/password to edit -- the connection is fully OAuth/WIF-driven, so
  // every text field is hidden. A one-connector list today (googlebigquery); add a class here when it
  // likewise has no editable connection fields. Any class not listed shows all fields.
  const FIELDLESS_CONNECTION_TYPES: ReadonlySet<string> = new Set(['googlebigquery']);

  export function connectionFieldApplicability(connectionType: string | undefined): ConnectionFieldApplicability {
    const showAllFields = !connectionType || !FIELDLESS_CONNECTION_TYPES.has(connectionType);
    return {
      serverName: showAllFields,
      serverPort: showAllFields,
      username: showAllFields,
      embeddedPassword: showAllFields
    };
  }

  // W-22483230: narrows a stored workgroupAuthMode wire string to a WifAuthMode, or undefined when it
  // is absent or not a recognized WIF value. The server sends this untyped, so the dialog validates it
  // here before treating the connection as WIF. Recognizes WIF_STANDARD as well as WIF_IMPERSONATION:
  // the UI chooser only offers impersonation today (BigQuery), but a stored standard-WIF connection
  // (Snowflake, once its server support lands, or one authored via Desktop) must hydrate as WIF and
  // re-save as standard WIF rather than silently reverting to OAuth.
  export function toWifAuthMode(workgroupAuthMode: string | undefined): WifAuthMode | undefined {
    if (workgroupAuthMode === WIF_IMPERSONATION || workgroupAuthMode === WIF_STANDARD) {
      return workgroupAuthMode;
    }
    return undefined;
  }

  // W-22483245: matches the GCP-style id rule. Returns true for blank so callers can apply their own
  // per-mode "required" check separately.
  export function isValidServiceAccount(serviceAccount: string | undefined): boolean {
    return !serviceAccount || SERVICE_ACCOUNT_REGEX.test(serviceAccount);
  }

  // W-22482525 / W-23640221: whether the Test Connection button must be disabled for this row. The
  // server-side check only exercises the real credential for password-style auth; for WIF it runs a
  // tokenless probe that never stamps federation claims (checkConnectionForWif), and for OAuth the
  // probe does not reflect the saved/embedded credential -- either way it can report a misleading
  // success. A credential-less (none) row has nothing to test, and a remote-query-agent (bridge) row
  // routes through the agent rather than a direct server-side probe. So Test Connection is disabled
  // for WIF, OAuth, none, and remote-query-agent, and stays active for every other auth type
  // (password, keypair, ...), whose check the server genuinely implements.
  // The OAuth/none/remote-query-agent checks only apply when the row's auth chooser is applicable
  // (authChooserApplies): on the unified dialog's plain database rows, credentialOption is always the
  // unused 'none' placeholder set in EditConnectionAction, and that dialog flavor tested bridge-backed
  // password connections successfully before the WIF work -- neither placeholder nor the pre-existing
  // bridge toggle should disable the button there (W-23840703). The OAuth-specific dialog always
  // passes authChooserApplies=true, so its own bridge-disables-Test-Connection rule is unchanged.
  export function isTestConnectionDisabledForAuth(
    state: Pick<EditConnectionDialogState, 'workgroupAuthMode' | 'credentialOption' | 'usingRemoteQueryAgent'>,
    authChooserApplies: boolean
  ): boolean {
    const isWif = state.workgroupAuthMode === WIF_IMPERSONATION || state.workgroupAuthMode === WIF_STANDARD;
    const isOAuthOrNoCredential = state.credentialOption === 'oauth' || state.credentialOption === 'none';
    return isWif || (authChooserApplies && (isOAuthOrNoCredential || state.usingRemoteQueryAgent === true));
  }

  // Stored-WIF can offer embedded OAuth only when a selectable credential (or Add New) exists and
  // the row is not managed keychain. W-23514330.
  export function canOfferOAuthForStoredWif(
    isStoredWif: boolean,
    usingManagedKeychain: boolean,
    hasSelectableCredentialOrAddNew: boolean
  ): boolean {
    return !isStoredWif || (!usingManagedKeychain && hasSelectableCredentialOrAddNew);
  }

  export function oauthEmbeddedRadioDisabled(args: {
    isStoredWif: boolean;
    usingManagedKeychain: boolean;
    credentials?: ReadonlyArray<unknown> | null;
    addNewOAuthCredentialOptionValue?: string | null;
  }): boolean {
    const hasSelectable =
      (!!args.credentials && args.credentials.length > 0) || !!args.addNewOAuthCredentialOptionValue;
    return !canOfferOAuthForStoredWif(args.isStoredWif, !!args.usingManagedKeychain, hasSelectable)
      || ((!args.credentials || args.credentials.length === 0) && !args.addNewOAuthCredentialOptionValue);
  }

  // Save for stored-WIF + oauth-embedded requires a credentialId so clear+attach cannot become NoAuth.
  export function isWifToOauthSaveAllowed(isStoredWif: boolean | undefined, credentialId: string | undefined | null): boolean {
    return !isStoredWif || credentialId != null;
  }

  // W-22483245: whether the dialog is in a valid state to Save / Test for the WIF portion.
  // Impersonation requires a well-formed service account; standard WIF forbids one.
  export function isWifStateValid(dialogState: EditConnectionDialogState): boolean {
    if (dialogState.workgroupAuthMode === WIF_IMPERSONATION) {
      return !!dialogState.authFederationServiceAccount && SERVICE_ACCOUNT_REGEX.test(dialogState.authFederationServiceAccount);
    }
    if (dialogState.workgroupAuthMode === WIF_STANDARD) {
      return !dialogState.authFederationServiceAccount;
    }
    return true;
  }

  // Used by the filebased Edit Connection path, which is a single bulk update and so is genuinely
  // all-or-nothing: any error reports complete failure with the first message.
  export function notifyResult(result: IResult, connections: EditableConnection[]) {
    if (result && result.errors && result.errors.length) {
      ContentActionNotification.displayFailureToast(errorMessage(result.errors[0]));
    } else {
      ContentActionNotification.displaySuccessToast(
        Messages.updateConnectionsSuccess({
          ITEM_COUNT: connections.length,
          ITEM_COUNT_LOC: Formatters.formatCount(connections.length)
        })
      );
    }
  }

  // W-22483230: per-row updates can succeed partially, so report a single summary toast (saved count
  // on full success; failed/total count plus the first error otherwise) rather than one per row.
  export function notifyAggregatedResult(results: IResult[]) {
    const failures = results.filter((result) => result && result.errors && result.errors.length);
    if (failures.length === 0) {
      ContentActionNotification.displaySuccessToast(
        Messages.updateConnectionsSuccess({
          ITEM_COUNT: results.length,
          ITEM_COUNT_LOC: Formatters.formatCount(results.length)
        })
      );
      return;
    }

    const firstError = errorMessage(failures[0].errors[0]);
    ContentActionNotification.displayFailureToast(
      Messages.updateConnectionsPartialFailure({
        FAILED_COUNT: failures.length,
        FAILED_COUNT_LOC: Formatters.formatCount(failures.length),
        TOTAL_COUNT_LOC: Formatters.formatCount(results.length),
        ERROR_MESSAGE: firstError
      })
    );
  }

  export function editConnectionDialogStateToConnectionUpdate(editConnectionDialogState: EditConnectionDialogState): IConnectionUpdate {
    const isWif = editConnectionDialogState.workgroupAuthMode === WIF_IMPERSONATION
      || editConnectionDialogState.workgroupAuthMode === WIF_STANDARD;
    return {
      server: isMixed(editConnectionDialogState.serverName) ? undefined : editConnectionDialogState.serverName,
      port: isMixed(editConnectionDialogState.serverPort) ? undefined : editConnectionDialogState.serverPort,
      username: isMixed(editConnectionDialogState.username) ? undefined : editConnectionDialogState.username,
      // W-23547174: never emit a password payload under WIF. The federation token replaces the
      // password, the dialog hides the Password toggle in WIF mode, and the server drops this field
      // on a WIF update anyway; suppressing it here keeps the request payload honest.
      passwordUpdate: isWif || _.isUndefined(editConnectionDialogState.isPasswordEmbedded) ? undefined : { isEmbedded: editConnectionDialogState.isPasswordEmbedded, newPassword: editConnectionDialogState.newPassword },
      usingRemoteQueryAgent: editConnectionDialogState.usingRemoteQueryAgent,
      // W-22483245: only emit WIF fields when a WIF mode is selected. The service account is sent
      // only for impersonation (and only when set); standard WIF must not carry one.
      workgroupAuthMode: isWif ? editConnectionDialogState.workgroupAuthMode : undefined,
      authFederationServiceAccount: editConnectionDialogState.workgroupAuthMode === WIF_IMPERSONATION
        ? editConnectionDialogState.authFederationServiceAccount
        : undefined
    };
  }

  function updateTestConnectionResult(serverResponse: ICheckConnectionResult): ITestConnectionResultReact {
    return {
      isPending: false,
      succeeded: serverResponse.succeeded,
      messages: serverResponse.messages,
    };
  }

  function errorMessage(error: IError): string {
    switch (error.code) {
      case CommonErrorCode.DatasourceNotFound:
      case CommonErrorCode.DataConnectionNotFound:
        return Messages.updateConnectionsError_datasourceNotFound();
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return Messages.updateConnectionsError_insufficientPermissions();
      case DataConnectionErrorCode.UpdateConnectionsInvalidPort:
        return Messages.updateConnectionsError_invalidPort();
      default:
        // Surface the server's own message for codes we don't map (e.g. a rejected workgroupAuthMode),
        // so the user sees the actual reason in the toast rather than a generic string.
        return error && error.message ? error.message : Messages.errorUnknown();
    }
  }

  async function checkCubeDatasource(datasource: IDataSourceInfo) {
    try {
      const datasourceDetailInfo: IDataSourceDetailInfo = await DataSources.getById(datasource.id);
      return datasourceDetailInfo.isHierarchical;
    }
    catch (e) {
      return false;
    }
  }
}
